import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { levelMeta } from "../../lib/scoring";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { FileText, TrendingUp, QrCode, Eye } from "lucide-react";

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/candidate/interviews").then((r) => setItems(r.data));
  }, []);

  if (!items) return <div className="text-ink-soft">Loading…</div>;
  const completed = items.filter((i) => i.status === "completed").sort((a, b) => a.created_at.localeCompare(b.created_at));
  const chart = completed.map((c, i) => ({ n: i + 1, overall: c.overall, date: c.date }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Interview History</h1>
        <p className="text-ink-soft text-sm mt-1">Track every interview and measure your progress over time.</p>
      </div>

      {chart.length > 1 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" /> Score Progression</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chart} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="n" tickFormatter={(n) => `#${n}`} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="overall" stroke="#0055FF" strokeWidth={3} dot={{ r: 5, fill: "#0055FF", strokeWidth: 2, stroke: "#fff" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Job Role</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Score</th><th className="px-5 py-3">Readiness</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Report</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((iv) => {
                const meta = levelMeta(iv.status === "completed" ? iv.overall : null);
                return (
                  <tr key={iv.id} className="hover:bg-slate-50 transition-colors" data-testid={`history-row-${iv.id}`}>
                    <td className="px-5 py-3.5 num text-ink-soft">{iv.date}</td>
                    <td className="px-5 py-3.5 font-semibold text-ink">{iv.role_name}</td>
                    <td className="px-5 py-3.5 text-ink-soft">{iv.type === "ai" ? "AI Interview" : "Expert Interview"}</td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const mode = iv.mode || (iv.type === "ai" ? "video" : "human");
                        const icon = iv.mode_icon || (mode === "voice" ? "📞" : mode === "human" ? "👤" : "🎥");
                        const label = mode === "voice" ? "AI Voice" : mode === "human" ? "Human Expert" : "AI Video";
                        return <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-ink-soft" data-testid={`history-mode-${iv.id}`}>{icon} {label}</span>;
                      })()}
                    </td>
                    <td className="px-5 py-3.5 num font-bold text-ink">{iv.status === "completed" ? iv.overall : "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: meta.bg, color: meta.text }}>{meta.label}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold ${iv.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{iv.status === "completed" ? "Completed" : "Scheduled"}</span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {iv.status === "completed" ? (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/candidate/result/${iv.id}`)} data-testid={`view-report-${iv.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="View Report"><Eye className="h-4 w-4" /></button>
                          <Link to={`/scorecard/${iv.verification_id}`} target="_blank" className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="QR Scorecard"><QrCode className="h-4 w-4" /></Link>
                        </div>
                      ) : (
                        <button onClick={() => navigate(`/candidate/interview/${iv.id}`)} className="text-xs font-bold text-brand">Attend</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-ink-soft">No interviews yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
